import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Slider } from "../components/ui/slider";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ShieldCheck, Check, X, Loader2, RefreshCw, AlertTriangle, ArrowLeft, Info, UserPlus, Sparkles, Search } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Score → Tailwind color helper
const scoreClass = (score) => {
  if (score >= 80) return "bg-emerald-100 text-emerald-800 border-emerald-300";
  if (score >= 65) return "bg-amber-100 text-amber-800 border-amber-300";
  if (score >= 40) return "bg-orange-100 text-orange-800 border-orange-300";
  return "bg-slate-100 text-slate-600 border-slate-300";
};

export default function FuzzyReconciliation() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ groups: [], total_groups: 0, total_orphan_sales: 0, auto_resolvable_count: 0 });
  const [minScore, setMinScore] = useState(30);
  const [searchTerm, setSearchTerm] = useState("");
  const [acceptingKey, setAcceptingKey] = useState(null);
  const [rejectingKey, setRejectingKey] = useState(null);
  // Create-agent dialog state
  const [createDialog, setCreateDialog] = useState(null); // { group, preview, form, submitting }
  // Iteration 20 — bulk wizard state
  const [bulkDialog, setBulkDialog] = useState(null); // { phase: 'dry'|'running'|'done', preview, result, forceConflicts }

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/reconciliation/fuzzy-suggestions`, {
        params: { min_score: minScore, top_n: 3 },
      });
      setData(res.data);
    } catch (e) {
      toast.error("Eroare la încărcarea sugestiilor fuzzy");
    } finally {
      setLoading(false);
    }
  }, [minScore]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const groupKey = (g) => `${g.source_system}|${g.raw_agent_code || ""}|${g.raw_agent_full_name || ""}`;

  const handleAccept = async (g, suggestion) => {
    const key = groupKey(g);
    setAcceptingKey(key);
    try {
      const res = await axios.post(`${API_URL}/reconciliation/accept-fuzzy-match`, {
        source_system: g.source_system,
        raw_agent_code: g.raw_agent_code,
        raw_agent_full_name: g.raw_agent_full_name,
        target_agent_id: suggestion.agent_id,
        score: suggestion.score,
        save_alias: true,
      });
      toast.success(`✓ ${res.data.sales_updated} vânzări asociate către ${suggestion.agent_name}`);
      await fetchSuggestions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la acceptare");
    } finally {
      setAcceptingKey(null);
    }
  };

  // Iteration 20 — bulk wizard handlers
  const openBulkWizard = async () => {
    setBulkDialog({ phase: "dry", preview: null, loading: true, forceConflicts: false });
    try {
      const res = await axios.post(`${API_URL}/reconciliation/bulk-create-agents`, {
        dry_run: true,
        min_sales_threshold: 1,
        skip_on_conflict: true,
        force_on_conflict: false,
      });
      setBulkDialog({ phase: "dry", preview: res.data, loading: false, forceConflicts: false });
    } catch (e) {
      toast.error("Eroare la dry-run");
      setBulkDialog(null);
    }
  };

  const runBulkCreate = async () => {
    if (!bulkDialog) return;
    setBulkDialog({ ...bulkDialog, phase: "running" });
    try {
      const res = await axios.post(`${API_URL}/reconciliation/bulk-create-agents`, {
        dry_run: false,
        min_sales_threshold: 1,
        skip_on_conflict: !bulkDialog.forceConflicts,
        force_on_conflict: bulkDialog.forceConflicts,
      });
      setBulkDialog({ ...bulkDialog, phase: "done", result: res.data });
      toast.success(`✓ Bulk: ${res.data.created} agenți creați, ${res.data.total_sales_relinked} vânzări asociate`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la rulare bulk");
      setBulkDialog({ ...bulkDialog, phase: "done", result: { error: true } });
    }
  };

  const closeBulkAndRefresh = async () => {
    setBulkDialog(null);
    await fetchSuggestions();
  };

  const handleReject = async (g) => {
    const key = groupKey(g);
    setRejectingKey(key);
    try {
      await axios.post(`${API_URL}/reconciliation/reject-fuzzy-match`, {
        source_system: g.source_system,
        raw_agent_code: g.raw_agent_code,
        raw_agent_full_name: g.raw_agent_full_name,
        reason: "Marcat manual: niciun match valid",
      });
      toast.success("Grup marcat ca neasociat (ascuns din sugestii)");
      await fetchSuggestions();
    } catch (e) {
      toast.error("Eroare la respingere");
    } finally {
      setRejectingKey(null);
    }
  };

  // Iteration 19 Opțiunea A — Create new agent from this group
  const openCreateDialog = async (g) => {
    setCreateDialog({ group: g, preview: null, form: null, loading: true, submitting: false });
    try {
      const res = await axios.get(`${API_URL}/reconciliation/preview-create-agent`, {
        params: {
          source_system: g.source_system,
          raw_agent_full_name: g.raw_agent_full_name || undefined,
          raw_agent_code: g.raw_agent_code || undefined,
        },
      });
      setCreateDialog({
        group: g,
        preview: res.data,
        form: {
          first_name: res.data.proposed.first_name || "",
          last_name: res.data.proposed.last_name || "",
          primary_marca_code: res.data.proposed.primary_marca_code || "",
          active: true,
        },
        loading: false,
        submitting: false,
      });
    } catch (e) {
      toast.error("Eroare la preview");
      setCreateDialog(null);
    }
  };

  const submitCreateAgent = async (force = false) => {
    if (!createDialog?.form) return;
    setCreateDialog((c) => ({ ...c, submitting: true }));
    try {
      const { group, form } = createDialog;
      const res = await axios.post(`${API_URL}/reconciliation/create-agent-from-group`, {
        source_system: group.source_system,
        raw_agent_code: group.raw_agent_code,
        raw_agent_full_name: group.raw_agent_full_name,
        first_name: form.first_name,
        last_name: form.last_name,
        primary_marca_code: form.primary_marca_code || null,
        active: form.active,
        force,
      });
      toast.success(`✓ Agent creat • ${res.data.sales_updated} vânzări asociate • ${res.data.alias_count} alias-uri`);
      setCreateDialog(null);
      await fetchSuggestions();
    } catch (e) {
      // 409 conflict — show conflicts inline; user can confirm with force=true
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.conflicts) {
        setCreateDialog((c) => ({ ...c, submitting: false, serverConflicts: detail.conflicts }));
      } else {
        toast.error(detail?.message || detail || "Eroare la creare agent");
        setCreateDialog((c) => ({ ...c, submitting: false }));
      }
    }
  };

  const handleBulkAcceptHighConfidence = async () => {
    const high = data.groups.filter((g) => g.suggestions.length > 0 && g.suggestions[0].score >= 90);
    if (high.length === 0) {
      toast.info("Niciun grup cu scor ≥ 90 disponibil");
      return;
    }
    if (!window.confirm(`Acceptă automat ${high.length} grupuri (scor ≥ 90)? Acțiunea este audit-loguită.`)) return;
    let ok = 0;
    for (const g of high) {
      try {
        await axios.post(`${API_URL}/reconciliation/accept-fuzzy-match`, {
          source_system: g.source_system,
          raw_agent_code: g.raw_agent_code,
          raw_agent_full_name: g.raw_agent_full_name,
          target_agent_id: g.suggestions[0].agent_id,
          score: g.suggestions[0].score,
          save_alias: true,
        });
        ok += 1;
      } catch (_) {}
    }
    toast.success(`Bulk: ${ok}/${high.length} grupuri rezolvate`);
    await fetchSuggestions();
  };

  const histogram = useMemo(() => {
    const buckets = { "≥90": 0, "80-89": 0, "65-79": 0, "40-64": 0, "<40": 0 };
    data.groups.forEach((g) => {
      const s = g.top_score || 0;
      if (s >= 90) buckets["≥90"] += 1;
      else if (s >= 80) buckets["80-89"] += 1;
      else if (s >= 65) buckets["65-79"] += 1;
      else if (s >= 40) buckets["40-64"] += 1;
      else buckets["<40"] += 1;
    });
    return buckets;
  }, [data.groups]);

  // Filtrare listă după căutare (nume/cod agent raw, sursă, sugestii)
  const filteredGroups = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return data.groups;
    return data.groups.filter((g) => {
      const hay = [
        g.raw_agent_full_name,
        g.raw_agent_code,
        g.source_system,
        ...(g.suggestions || []).map((s) => s.agent_name),
        ...(g.suggestions || []).map((s) => s.primary_marca_code),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [data.groups, searchTerm]);

  return (
    <div className="space-y-6" data-testid="fuzzy-reconciliation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
            <Link to="/reconciliation" className="hover:text-slate-700 flex items-center gap-1">
              <ArrowLeft className="h-3.5 w-3.5" /> Reconciliere
            </Link>
            <span>/</span>
            <span>Fuzzy</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-indigo-600" />
            Reconciliere Fuzzy Agenți
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Sugestii ranked de match pentru vânzările cu <code>agent_ref_id IS NULL</code>. Acceptarea creează automat alias permanent + asociază toate vânzările din grup.
          </p>
        </div>
        <Button onClick={fetchSuggestions} variant="outline" size="sm" data-testid="refresh-btn" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Reîncarcă
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs uppercase text-slate-500 font-medium">Grupuri Orfane</p>
            <p className="text-3xl font-bold text-slate-900" data-testid="kpi-groups">{data.total_groups}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs uppercase text-slate-500 font-medium">Vânzări Orfane</p>
            <p className="text-3xl font-bold text-amber-600" data-testid="kpi-orphans">{data.total_orphan_sales}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs uppercase text-slate-500 font-medium">Auto-rezolvabile (≥80)</p>
            <p className="text-3xl font-bold text-emerald-600" data-testid="kpi-auto-resolvable">{data.auto_resolvable_count}</p>
            <p className="text-xs text-slate-500 mt-1">vânzări cu match high-confidence</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs uppercase text-slate-500 font-medium">Distribuție Scoruri</p>
            <div className="flex gap-1 mt-2 text-xs">
              {Object.entries(histogram).map(([k, v]) => (
                <div key={k} className={`px-2 py-1 rounded ${v > 0 ? "bg-slate-100" : "bg-slate-50 text-slate-400"}`}>
                  <span className="font-semibold">{v}</span> <span>{k}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="flex justify-between text-xs mb-1">
                <span className="font-medium">Scor minim de afișare</span>
                <span className="text-slate-500">{minScore}</span>
              </div>
              <Slider
                value={[minScore]}
                onValueChange={(v) => setMinScore(v[0])}
                min={0}
                max={100}
                step={5}
                data-testid="score-slider"
              />
            </div>
            <Button
              onClick={openBulkWizard}
              variant="default"
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="bulk-create-agents-btn"
              disabled={loading}
            >
              <Sparkles className="h-4 w-4 mr-2" /> Curăță toate non-conflict
            </Button>
            <Button
              onClick={handleBulkAcceptHighConfidence}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="bulk-accept-btn"
              disabled={loading || histogram["≥90"] === 0}
            >
              <Check className="h-4 w-4 mr-2" /> Acceptă toate cu scor ≥ 90 ({histogram["≥90"]})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info banner when no high-confidence */}
      {!loading && data.auto_resolvable_count === 0 && data.total_groups > 0 && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-4 flex gap-3" data-testid="info-banner">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <strong>Niciun grup cu scor ≥ 80.</strong> Acest lucru sugerează că agenții raw din aceste vânzări <em>nu există încă</em> în tabela <code>agents</code>. Pentru rezolvare istorică ar fi necesar să se creeze profile noi de agent. Pagina rămâne utilă pentru a respinge grupurile (audit) și pentru viitoarele import-uri cu typo-uri minore.
          </div>
        </div>
      )}

      {/* Groups table */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Grupuri Orfane &amp; Sugestii</CardTitle>
              <CardDescription>Top 3 candidate sortate descrescător după scor combinat (Levenshtein 70% + Jaccard tokens 30%).</CardDescription>
            </div>
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Caută agent (nume, cod, sursă)…"
                className="pl-8 h-9"
                data-testid="fuzzy-search-input"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="fuzzy-search-clear"
                  aria-label="Șterge căutarea"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          {searchTerm && !loading && (
            <p className="text-xs text-slate-500 mt-1" data-testid="fuzzy-search-count">
              {filteredGroups.length} din {data.groups.length} grupuri
            </p>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : data.groups.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ShieldCheck className="h-12 w-12 mx-auto text-emerald-500 mb-2" />
              Niciun grup orfan rămas — totul este reconciliat ✓
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="fuzzy-search-empty">
              Niciun grup nu corespunde căutării „<strong>{searchTerm}</strong>".
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Sursă</TableHead>
                  <TableHead>Raw Agent</TableHead>
                  <TableHead className="text-right w-[80px]">Vânzări</TableHead>
                  <TableHead>Top Sugestii</TableHead>
                  <TableHead className="w-[170px] text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredGroups.map((g) => {
                  const key = groupKey(g);
                  return (
                    <TableRow key={key} data-testid={`group-row-${key}`}>
                      <TableCell>
                        <Badge variant="outline" className="uppercase text-[10px]">
                          {g.source_system}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">{g.raw_agent_full_name || <em className="text-slate-400">(fără nume)</em>}</div>
                        {g.raw_agent_code && (
                          <div className="text-xs text-slate-500 mt-0.5">cod: <code>{g.raw_agent_code}</code></div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary" className="font-mono">{g.count}</Badge>
                      </TableCell>
                      <TableCell>
                        {g.suggestions.length === 0 ? (
                          <span className="text-xs text-slate-400 italic">— niciun candidat peste pragul de scor —</span>
                        ) : (
                          <div className="space-y-1">
                            {g.suggestions.map((s) => (
                              <div key={s.agent_id} className="flex items-center gap-2">
                                <span
                                  className={`text-[11px] px-1.5 py-0.5 rounded border font-mono ${scoreClass(s.score)}`}
                                  title={`Edit distance: ${s.distance}`}
                                >
                                  {s.score.toFixed(0)}
                                </span>
                                <span className="text-sm">{s.agent_name}</span>
                                {s.primary_marca_code && (
                                  <span className="text-xs text-slate-400 font-mono">[{s.primary_marca_code}]</span>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => handleAccept(g, s)}
                                  disabled={acceptingKey === key}
                                  data-testid={`accept-btn-${key}-${s.agent_id}`}
                                >
                                  {acceptingKey === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" /> Acceptă</>}
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-indigo-600 hover:bg-indigo-50"
                            onClick={() => openCreateDialog(g)}
                            data-testid={`create-agent-btn-${key}`}
                          >
                            <UserPlus className="h-3 w-3 mr-1" /> Creează
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-500 hover:text-rose-600 hover:bg-rose-50"
                            onClick={() => handleReject(g)}
                            disabled={rejectingKey === key}
                            data-testid={`reject-btn-${key}`}
                          >
                            {rejectingKey === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <><X className="h-3 w-3" /></>}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create-agent-from-group dialog */}
      <Dialog open={!!createDialog} onOpenChange={(o) => !o && setCreateDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="create-agent-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-indigo-600" /> Creează agent nou din grup
            </DialogTitle>
            <DialogDescription>
              Va crea un profil nou în <code>agents</code>, va salva alias-urile pentru sursa
              <Badge variant="outline" className="mx-1 uppercase text-[10px]">{createDialog?.group?.source_system}</Badge>
              și va asocia toate vânzările orfane din acest grup.
            </DialogDescription>
          </DialogHeader>

          {createDialog?.loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : createDialog ? (
            <div className="space-y-4">
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm" data-testid="impact-preview">
                <div className="flex items-center gap-2 text-emerald-900">
                  <Check className="h-4 w-4" />
                  <span>
                    <strong>{createDialog.preview?.impact?.sales_to_link ?? 0}</strong> vânzări vor fi asociate noului agent.
                  </span>
                </div>
              </div>

              {(createDialog.preview?.has_conflicts || createDialog.serverConflicts) && (
                <div className="rounded-md bg-amber-50 border border-amber-300 p-3 text-sm" data-testid="conflict-warning">
                  <div className="flex items-start gap-2 text-amber-900">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Atenție — conflict detectat:</strong>
                      <ul className="list-disc ml-5 mt-1 text-xs">
                        {(createDialog.serverConflicts || createDialog.preview.conflicts).map((c, i) => (
                          <li key={i}>
                            <span className="font-mono text-[10px] uppercase">{c.kind}</span> · {c.full_name}
                            {c.primary_marca_code && <> · <code>{c.primary_marca_code}</code></>}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-1 text-xs">Dacă forțezi, marca duplicată va fi omisă automat (alias-urile rămân).</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nume (last_name)</Label>
                  <Input
                    value={createDialog.form.last_name}
                    onChange={(e) => setCreateDialog((c) => ({ ...c, form: { ...c.form, last_name: e.target.value } }))}
                    data-testid="form-last-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Prenume (first_name)</Label>
                  <Input
                    value={createDialog.form.first_name}
                    onChange={(e) => setCreateDialog((c) => ({ ...c, form: { ...c.form, first_name: e.target.value } }))}
                    data-testid="form-first-name"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Cod Marca primar (opțional)</Label>
                  <Input
                    value={createDialog.form.primary_marca_code}
                    onChange={(e) => setCreateDialog((c) => ({ ...c, form: { ...c.form, primary_marca_code: e.target.value } }))}
                    placeholder="ex: RE12345"
                    className="font-mono"
                    data-testid="form-marca-code"
                  />
                </div>
              </div>

              <div className="rounded-md bg-slate-50 border text-xs p-2 font-mono space-y-0.5">
                <div><span className="text-slate-400">raw_code:</span> {createDialog.group.raw_agent_code || <em className="text-slate-400">none</em>}</div>
                <div><span className="text-slate-400">raw_name:</span> {createDialog.group.raw_agent_full_name || <em className="text-slate-400">none</em>}</div>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(null)} data-testid="cancel-create-btn">Anulează</Button>
            {createDialog?.preview?.has_conflicts || createDialog?.serverConflicts ? (
              <Button
                onClick={() => submitCreateAgent(true)}
                disabled={createDialog?.submitting}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="force-create-btn"
              >
                {createDialog?.submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
                Forțează &amp; creează
              </Button>
            ) : (
              <Button
                onClick={() => submitCreateAgent(false)}
                disabled={createDialog?.submitting || !createDialog?.form}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="confirm-create-btn"
              >
                {createDialog?.submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                Creează &amp; asociază
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iteration 20 — Bulk Create Wizard */}
      <Dialog open={!!bulkDialog} onOpenChange={(o) => !o && setBulkDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-wizard-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              {bulkDialog?.phase === "done"
                ? "Raport Bulk Curățare"
                : bulkDialog?.phase === "running"
                ? "Procesare în curs..."
                : "Bulk: Curăță toate non-conflict"}
            </DialogTitle>
            <DialogDescription>
              {bulkDialog?.phase === "done"
                ? "Rezultatul final al rulării bulk. Audit log creat cu batch_action_id."
                : "Va crea automat câte un agent pentru fiecare grup fără conflict + va asocia vânzările. Tranzacție per grup."}
            </DialogDescription>
          </DialogHeader>

          {/* PHASE: dry-run preview */}
          {bulkDialog?.phase === "dry" && (
            bulkDialog.loading ? (
              <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3">
                    <p className="text-xs uppercase text-emerald-700 font-medium">Vor fi create</p>
                    <p className="text-3xl font-bold text-emerald-900" data-testid="dry-created">{bulkDialog.preview?.created || 0}</p>
                    <p className="text-xs text-emerald-700/80">agenți noi</p>
                  </div>
                  <div className="rounded-md bg-blue-50 border border-blue-200 p-3">
                    <p className="text-xs uppercase text-blue-700 font-medium">Vor fi relinkate</p>
                    <p className="text-3xl font-bold text-blue-900" data-testid="dry-sales">{bulkDialog.preview?.total_sales_relinked || 0}</p>
                    <p className="text-xs text-blue-700/80">vânzări</p>
                  </div>
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
                    <p className="text-xs uppercase text-amber-700 font-medium">Vor fi sărite</p>
                    <p className="text-3xl font-bold text-amber-900" data-testid="dry-skipped">{bulkDialog.preview?.skipped_conflicts || 0}</p>
                    <p className="text-xs text-amber-700/80">conflicte / raw gol</p>
                  </div>
                </div>

                {bulkDialog.preview?.skipped_conflicts > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
                    <strong className="text-amber-900">Grupuri sărite:</strong>
                    <ul className="mt-1 ml-4 list-disc text-xs text-amber-800 max-h-32 overflow-y-auto">
                      {bulkDialog.preview.groups_detail.filter((g) => g.status === "skipped").map((g, i) => (
                        <li key={i}>
                          <code>{g.raw_agent_full_name || "(empty)"}</code>
                          {" "}({g.sales_count} vânzări) — <em>{g.reason}</em>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="force-conflicts-cb"
                    checked={bulkDialog.forceConflicts}
                    onChange={(e) => setBulkDialog({ ...bulkDialog, forceConflicts: e.target.checked })}
                    data-testid="force-conflicts-checkbox"
                  />
                  <label htmlFor="force-conflicts-cb" className="text-sm">
                    Forțează inclusiv conflictele de nume (marca duplicată va fi omisă automat)
                  </label>
                </div>

                <div className="text-xs text-slate-500">
                  Durata estimată dry-run: <code>{bulkDialog.preview?.duration_ms}ms</code> · Real run de obicei x2-3.
                </div>
              </div>
            )
          )}

          {/* PHASE: running */}
          {bulkDialog?.phase === "running" && (
            <div className="py-12 flex flex-col items-center gap-3">
              <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
              <p className="text-sm text-slate-600">Se creează agenții și se relinkează vânzările...</p>
              <p className="text-xs text-slate-400">Tranzacție per grup; nu închide tab-ul.</p>
            </div>
          )}

          {/* PHASE: done */}
          {bulkDialog?.phase === "done" && bulkDialog.result && !bulkDialog.result.error && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-2">
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2 text-center">
                  <p className="text-2xl font-bold text-emerald-900" data-testid="result-created">{bulkDialog.result.created}</p>
                  <p className="text-[10px] uppercase text-emerald-700">Creați</p>
                </div>
                <div className="rounded-md bg-blue-50 border border-blue-200 p-2 text-center">
                  <p className="text-2xl font-bold text-blue-900" data-testid="result-sales">{bulkDialog.result.total_sales_relinked}</p>
                  <p className="text-[10px] uppercase text-blue-700">Vânzări linked</p>
                </div>
                <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-center">
                  <p className="text-2xl font-bold text-amber-900" data-testid="result-skipped">{bulkDialog.result.skipped_conflicts}</p>
                  <p className="text-[10px] uppercase text-amber-700">Sărite</p>
                </div>
                <div className="rounded-md bg-rose-50 border border-rose-200 p-2 text-center">
                  <p className="text-2xl font-bold text-rose-900" data-testid="result-errors">{bulkDialog.result.errors}</p>
                  <p className="text-[10px] uppercase text-rose-700">Erori</p>
                </div>
              </div>

              <div className="text-xs text-slate-600 font-mono">
                batch_action_id: <code className="bg-slate-100 px-1 py-0.5 rounded">{bulkDialog.result.batch_action_id}</code> · durată: <code>{bulkDialog.result.duration_ms}ms</code>
              </div>

              <div className="border rounded-md max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 w-20">Status</th>
                      <th className="text-left p-2">Raw Agent</th>
                      <th className="text-right p-2 w-16">Sales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkDialog.result.groups_detail.map((g, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2">
                          {g.status === "created" && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">Creat</Badge>}
                          {g.status === "skipped" && <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">Sărit</Badge>}
                          {g.status === "error" && <Badge className="bg-rose-100 text-rose-800 border-rose-300 text-[10px]">Eroare</Badge>}
                        </td>
                        <td className="p-2">
                          <div className="font-medium">{g.raw_agent_full_name || <em className="text-slate-400">(empty)</em>}</div>
                          {g.reason && <div className="text-[10px] text-slate-500 italic">{g.reason}</div>}
                          {g.agent_id && <div className="text-[10px] text-slate-400 font-mono">→ {g.agent_id.slice(0, 8)}</div>}
                        </td>
                        <td className="p-2 text-right font-mono">{g.sales_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {bulkDialog?.phase === "done" && bulkDialog.result?.error && (
            <div className="py-6 text-center text-rose-700">
              <X className="h-10 w-10 mx-auto mb-2" />
              <p>Eroare la rulare. Verifică logs.</p>
            </div>
          )}

          <DialogFooter>
            {bulkDialog?.phase === "dry" && !bulkDialog.loading && (
              <>
                <Button variant="outline" onClick={() => setBulkDialog(null)} data-testid="bulk-cancel-btn">Anulează</Button>
                <Button
                  onClick={runBulkCreate}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="bulk-confirm-btn"
                  disabled={!bulkDialog.preview?.created}
                >
                  <Sparkles className="h-4 w-4 mr-2" /> Confirmă &amp; rulează ({bulkDialog.preview?.created} agenți)
                </Button>
              </>
            )}
            {bulkDialog?.phase === "done" && (
              <Button onClick={closeBulkAndRefresh} className="bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-close-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Închide &amp; reîncarcă
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
